import { z } from 'zod';
import { idDeFormularioSchema } from '../escalas/catalogo.js';
import { respuestaSchema } from './tipos.js';
/**
 * Contrato de la API del motor de formularios.
 *
 * **La definición del formulario no viaja por la red.** La PWA importa
 * `@pasos/schemas` como cualquier otro módulo, así que tiene las preguntas
 * compiladas adentro: es lo que permite que el microcheck se responda sin
 * conexión (S-08) sin haber pedido nada antes. Por acá viaja sólo el **estado**
 * de la aplicación.
 *
 * `abrir` es **idempotente**: pedirlo dos veces para el mismo formulario e hito
 * devuelve la misma aplicación, no una segunda. Sin eso, la cola de envío
 * offline crearía una aplicación por reintento y la adherencia contaría de más.
 */
/**
 * Hito del protocolo (`D+28`, `M2`, `D+120`…).
 *
 * Formato acotado y sin espacios: es una clave que alinea la serie longitudinal
 * de M-15, y una que llegue con un espacio de más rompe la comparación en
 * silencio. La lista cerrada de hitos la fija el plan del programa en E3.3.
 */
export const hitoSchema = z
    .string()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9+_-]+$/, 'el hito no lleva espacios ni acentos');
export const abrirFormularioSchema = z.strictObject({
    formularioId: idDeFormularioSchema,
    hito: hitoSchema.nullable(),
});
/**
 * Respuestas a guardar.
 *
 * Se **fusionan** con lo que ya había: la PWA manda lo nuevo, no el estado
 * entero. Corregir una respuesta anterior es mandarla de nuevo con otro valor.
 */
export const guardarRespuestasSchema = z.strictObject({
    respuestas: z.record(z.string().min(1).max(30), respuestaSchema),
});
/**
 * Cómo se cierra.
 *
 * `completa` exige que no falte ninguna pregunta activa; el servidor lo verifica
 * y no confía en que el cliente lo haya hecho. `parcial` es el que quedó a
 * medias y todavía podría retomarse; `abandonada` la cierra sin vuelta.
 *
 * **`tardio` no viaja acá.** En E3.2 lo declaraba el cliente y siempre llegaba en
 * `false` (P-50). Ahora lo decide el servidor comparando contra el día del ítem
 * del plan: el cliente no tiene por qué opinar sobre si su propia respuesta
 * llegó tarde, y con la cadencia ya existe con qué compararla.
 */
export const cerrarFormularioSchema = z.strictObject({
    cierre: z.enum(['completa', 'parcial', 'abandonada']),
    /** Cuánto tardó. Lo mide el cliente, que es el único que sabe cuándo abrió. */
    duracionSegundos: z.number().int().nonnegative().max(86_400),
    /**
     * Cuándo terminó de responder, según el reloj del **dispositivo** (H-6).
     *
     * **No manda.** Sigue mandando el servidor: el reloj de un teléfono se cambia
     * a mano y se atrasa solo, así que `occurredAt` y el reloj del contacto (H-5)
     * no la miran. Se usa para una sola cosa: una respuesta hecha en término sin
     * señal, que sale de la cola dos días después, no se marca como tardía
     * (`llegoTarde`, con sus límites verificados del lado del servidor).
     *
     * Opcional: una respuesta hecha con conexión no la necesita, y un envío
     * encolado por una versión anterior de la app no la tiene.
     */
    respondidoEnDispositivoAt: z.iso.datetime().optional(),
});
export const estadoDeAplicacionSchema = z.enum(['en_curso', 'completa', 'parcial', 'abandonada']);
/**
 * El estado de una aplicación, tal como lo devuelve la API.
 *
 * `siguientePregunta` es el `linkId` que toca ahora — el cliente ya tiene la
 * pregunta— y `null` cuando no queda ninguna. `progreso` viene del servidor
 * para que la pantalla diga lo mismo que el registro, aunque el cliente esté
 * desactualizado.
 */
export const aplicacionSchema = z.strictObject({
    id: z.uuid(),
    /** Ítem del plan que esta aplicación responde, cuando vino de uno. */
    planItemId: z.uuid().nullable(),
    formularioId: z.string(),
    formularioVersion: z.string(),
    hito: z.string().nullable(),
    estado: estadoDeAplicacionSchema,
    respuestas: z.record(z.string(), respuestaSchema),
    siguientePregunta: z.string().nullable(),
    progreso: z.strictObject({
        numero: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        respondidas: z.number().int().nonnegative(),
    }),
    /** Sólo en una escala completa. */
    puntaje: z.number().nullable(),
    subpuntajes: z.record(z.string(), z.number()).nullable(),
});
//# sourceMappingURL=api.js.map